import { usePage } from '@inertiajs/react'

import Link from '@/components/link-component'
import CtaLink from '@/components/ui/cta-link'

import { useTranslations } from '@/utils/locale'
import { cx, getAltPath } from '@/utils/strings'
import { useMarkdownCopy, useAIChat } from '@/hooks/useMarkdownCopy'
import { TypeHelpTopNav } from '@/types/ctf'

type HelpUsefulLinksProps = {
  className?: string
  isHelpHome?: boolean
  links: TypeHelpTopNav<'WITHOUT_UNRESOLVABLE_LINKS'>['fields']['usefulLinks']
}

export default function HelpUsefulLinks({ className, isHelpHome, links }: HelpUsefulLinksProps) {
  const { t } = useTranslations()
  const { url } = usePage()
  const { copied, handleCopyMarkdown } = useMarkdownCopy()
  const { openAIChat } = useAIChat()

  // Build URLs using getAltPath
  const markdownUrl = getAltPath(url, '.md')
  const pdfPath = getAltPath(url, '.pdf')
  const isHelpArticle = url.includes('/help/') && !isHelpHome

  const copyActions = isHelpArticle
    ? [
        {
          action: () => handleCopyMarkdown(url),
          text: copied ? t('Copied!') : t('Copy for LLM'),
          unicode: '\ue905',
        },
        {
          action: () => openAIChat(url),
          text: t('Open AI Chat'),
          unicode: '\ue990',
        },
        {
          link: markdownUrl,
          text: t('Open page as Markdown'),
          unicode: '\ue94F',
          external: true,
        },
        {
          link: pdfPath,
          text: t('Download as PDF'),
          unicode: '\ue94e',
          external: true,
        },
      ]
    : []

  return (
    <ul className={cx('list-none', className)}>
      {links?.map((link) => {
        if (!link) return null
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { internalName, ...fields } = link.fields
        return (
          <li className="py-1" key={link.sys.id}>
            <CtaLink {...fields} to={fields.link?.fields.url} className="no-underline! hover:text-indigoBlue">
              {fields.label}
            </CtaLink>
          </li>
        )
      })}

      {/* Divider before copy actions */}
      {copyActions.length > 0 && (
        <li className="md:hidden">
          <div className="border-t border-1 border-secondary-500 w-full my-4" />
        </li>
      )}

      {/* Copy actions for help articles */}
      {copyActions.map((item, i) => (
        <li className="py-1 md:hidden" key={`copy-action-${i}`}>
          {item.action ? (
            <button onClick={item.action} className="flex items-center gap-2 w-full">
              <span className="flex items-end flex-row-reverse w-full">
                <span className="font-bwi ml-2 mr-0 text-sm">{item.unicode}</span>
                <span className="md:text-sm lg:text-base">{item.text}</span>
              </span>
            </button>
          ) : (
            <Link
              to={item.link}
              className="flex items-center gap-2"
              {...(item.external && { target: '_blank', rel: 'noopener noreferrer' })}
            >
              <span className="flex items-end flex-row-reverse">
                <span className="font-bwi ml-2 mr-0 text-sm">{item.unicode}</span>
                <span className="md:text-sm lg:text-base">{item.text}</span>
              </span>
            </Link>
          )}
        </li>
      ))}
    </ul>
  )
}
